import { OPERATORS } from '../../constants';
import { BaseOperatorStrategy } from './base.operator-strategy';
import { applyResolutionConstraintsFromMode } from '../../../components/shared/utils/resolution-constraints';
import type {
  ModeSelectionContext,
  SelectionBlockerArgs,
  SelectionBlockerResult,
} from '../operator-strategy';

/**
 * GEOSAT pricing model:
 *   base = selectedMode.unitCost + selectedLatency.upliftCost
 * then apply the existing factors (holdback/cloud/ONA) via the base class.
 */
export class GeosatOperatorStrategy extends BaseOperatorStrategy {
  readonly operatorKey = OPERATORS.GEOSAT;

  private readonly selectionBlockerRules: Array<
    (args: SelectionBlockerArgs) => SelectionBlockerResult | null
  > = [
    (args) => this.getRecurrencyBlocker(args),
    (args) => this.getWeekendSchedulingBlocker(args),
    (args) => this.getMinimumAreaBlocker(args),
  ];

  private formatAreaSqkm(value: number): string {
    return Number.isInteger(value)
      ? value.toString()
      : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }

  private getTierKey(sensorRow: any): string {
    return String(
      sensorRow?.selectedLatencyKey?.key ?? sensorRow?.selectedLatency ?? '',
    ).toLowerCase();
  }

  private getRecurrencyBlocker(
    args: SelectionBlockerArgs,
  ): SelectionBlockerResult | null {
    if (!args?.taskingContext?.isRecurrent) return null;

    return this.createUnsupportedRecurrencyBlocker(
      args?.sensorRow,
      'GEOSAT_RECURRENCY_UNSUPPORTED',
    );
  }

  private getWeekendSchedulingBlocker(
    args: SelectionBlockerArgs,
  ): SelectionBlockerResult | null {
    if (this.getTierKey(args?.sensorRow) !== 'standard') return null;

    const submissionDow = args?.submissionDateUtc?.getUTCDay?.();
    const toiStartDow = args?.toiStartUtc?.getUTCDay?.();

    if (!Number.isFinite(submissionDow) || !Number.isFinite(toiStartDow)) {
      return null;
    }

    const submissionIsFriOrWeekend =
      submissionDow === 5 || submissionDow === 6 || submissionDow === 0;
    const toiStartsWeekendOrMonday =
      toiStartDow === 6 || toiStartDow === 0 || toiStartDow === 1;

    if (!submissionIsFriOrWeekend || !toiStartsWeekendOrMonday) return null;

    return {
      code: 'GEOSAT_STD_WEEKEND_SCHEDULING',
      message:
        'The operator cannot accept the requested task because they do not schedule standard tasking on the weekend. Please choose your start time of interest (TOI) from Tuesday.',
    };
  }

  private getMinimumAreaBlocker(
    args: SelectionBlockerArgs,
  ): SelectionBlockerResult | null {
    const sensorRow = args?.sensorRow;
    const selectedAreaSqkm = Number(args?.taskingContext?.selectedAreaSqkm);
    const minimumAreaSqkm = Number(args?.taskingContext?.minimumAreaSqkm);

    if (
      !Number.isFinite(selectedAreaSqkm) ||
      !Number.isFinite(minimumAreaSqkm) ||
      selectedAreaSqkm <= 0 ||
      selectedAreaSqkm >= minimumAreaSqkm
    ) {
      return null;
    }

    const formattedMinimumArea = this.formatAreaSqkm(minimumAreaSqkm);
    const label =
      `${sensorRow?.displayName ?? 'GEOSAT'} ${sensorRow?.sensor ?? ''}`.trim();

    return {
      code: 'GEOSAT_MIN_AREA_BLOCKED',
      message: `The ${label} has a requirement for ${formattedMinimumArea} sqkm as the minimum task area. Your area of interest (AOI) is less than ${formattedMinimumArea} sqkm. 
        Kindly increase the size of the polygon or choose an alternative provider.`,
    };
  }

  /**
   * GEOSAT selection-time blockers:
   * Rules are evaluated in order and return on the first blocker.
   */
  getSelectionBlocker(
    args: SelectionBlockerArgs,
  ): SelectionBlockerResult | null {
    for (const rule of this.selectionBlockerRules) {
      const blocker = rule(args);
      if (blocker) {
        return blocker;
      }
    }

    return null;
  }

  private normalizeModeKey(value: any): string {
    return String(value ?? '')
      .split(':')[0]
      .trim()
      .replace(/\s+/g, ' ');
  }

  private getModeLabel(mode: any): string {
    return `${mode?.captureMode ?? ''} ${mode?.key ?? ''}`.toLowerCase();
  }

  private isSpotMode(mode: any): boolean {
    return this.getModeLabel(mode).includes('spot');
  }

  private isStripMode(mode: any): boolean {
    return this.getModeLabel(mode).includes('strip');
  }

  selectDefaultMode(
    collectionModes: any[] = [],
    context: ModeSelectionContext,
  ): any | null {
    if (!collectionModes?.length) return null;

    if (context?.targetType === 'AOI') {
      return collectionModes.find((mode) => this.isStripMode(mode)) || collectionModes[0];
    }

    return collectionModes.find((mode) => this.isSpotMode(mode)) || collectionModes[0];
  }

  isModeDisabled(mode: any, sensorRow: any, context: ModeSelectionContext): boolean {
    if (!mode || sensorRow?.key !== OPERATORS.GEOSAT) return false;

    if (context?.targetType === 'AOI') {
      return this.isSpotMode(mode);
    }

    return this.isStripMode(mode);
  }

  private getSelectedModeKey(sensorRow: any): string {
    return this.normalizeModeKey(
      sensorRow?.selectedModes?.[0]?.key ??
        sensorRow?.selectedModes?.[0]?.modeKey ??
        sensorRow?.selectedModes?.[0]?.mode,
    );
  }

  private getModeUnitCost(sensorRow: any): number {
    const selectedModeKey = this.getSelectedModeKey(sensorRow);

    let modeUnitCost = 0;
    if (Array.isArray(sensorRow?.pricing) && selectedModeKey) {
      const pricingItem = sensorRow.pricing.find(
        (p: any) => this.normalizeModeKey(p?.mode) === selectedModeKey,
      );
      modeUnitCost = Number(
        pricingItem?.unitcost ?? pricingItem?.unitCost ?? 0,
      );
    }

    // Backward compatibility: older payloads used resolution.unitCost.
    if (!modeUnitCost) {
      modeUnitCost = Number(sensorRow?.selectedResolution?.unitCost ?? 0);
    }

    return modeUnitCost;
  }

  override syncLatencyCosts(sensorRow: any): void {
    if (!sensorRow || !Array.isArray(sensorRow.latency)) return;

    const modeUnitCost = this.getModeUnitCost(sensorRow);

    for (const latencyItem of sensorRow.latency) {
      const upliftCost = Number(latencyItem?.upliftCost ?? 0);
      const newUnitCost = modeUnitCost + upliftCost;
      latencyItem.unitcost = newUnitCost;
      latencyItem.cost = newUnitCost;
    }
  }

  /**
   * Compute base cost for GEOSAT: mode.unitCost + latency.upliftCost.
   * Also syncs each latency option's unitcost/cost so UI dropdowns remain accurate.
   */
  protected computeBaseCost(
    sensorRow: any,
    latencyIndx: number,
  ): number | null {
    if (!sensorRow || !Array.isArray(sensorRow.latency)) return null;

    this.syncLatencyCosts(sensorRow);

    const selectedLatency = sensorRow.latency[latencyIndx];
    if (!selectedLatency) return null;

    return Number(selectedLatency.cost ?? 0);
  }

  /**
   * Apply mode-based resolution constraints for GEOSAT.
   * Each collection_mode includes a `resolution[]` whitelist.
   */
  override applyResolutionConstraints(
    sensorRow: any,
    productMode: any,
  ): boolean {
    return applyResolutionConstraintsFromMode(sensorRow, productMode);
  }
}
